/**
 * brokerFunds.service — currently-deployable cash per broker.
 * Backed by GET /v1/broker/funds (Round 67a). Server caches for 30s;
 * this client adds an in-memory layer so re-opens of the order-confirm
 * dialog within the same session don't refetch.
 */
import api from './api';

export type DeltaStatus = 'ok' | 'no_credentials' | 'error';
export type ZerodhaStatus = 'ok' | 'no_credentials' | 'no_token' | 'error';

export interface BrokerFunds {
  availableInr: number;
  byBroker: { delta: number; zerodha: number };
  status: { delta: DeltaStatus; zerodha: ZerodhaStatus };
  errors: { delta?: string; zerodha?: string };
  fetchedAt: number;
  cached: boolean;
}

let inflight: Promise<BrokerFunds | null> | null = null;
let lastResult: { ts: number; data: BrokerFunds } | null = null;
const CLIENT_TTL_MS = 15_000;

export const brokerFundsService = {
  async getFunds(opts?: { fresh?: boolean }): Promise<BrokerFunds | null> {
    const fresh = !!opts?.fresh;
    if (!fresh && lastResult && Date.now() - lastResult.ts < CLIENT_TTL_MS) {
      return lastResult.data;
    }
    if (inflight) return inflight;
    inflight = (async () => {
      try {
        const r = await api.get<{ success: boolean; data: BrokerFunds }>('/broker/funds', {
          params: fresh ? { fresh: 1 } : undefined,
          headers: { _suppressToast: 'true' },
        });
        const data = r.data?.data;
        if (data) lastResult = { ts: Date.now(), data };
        return data ?? null;
      } catch {
        return null;
      } finally {
        inflight = null;
      }
    })();
    return inflight;
  },

  /** Invalidate the local cache — call after a known broker reconnect or
   *  manual refresh control on the dialog. */
  invalidate() { lastResult = null; },
};
